import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { debounce } from "lodash";
import toast from "react-hot-toast";
import { Pagination, Tooltip, useMediaQuery } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import SoftBox from "../../components/SoftBox";
import SoftInput from "../../components/SoftInput";
import SoftButton from "../../components/SoftButton";
import SoftTypography from "../../components/SoftTypography";
import SoftProfile from "../../components/ProfilePictures/SoftProfile";
import PageHeader from "../../components/PageHeader";
import SoftConfirmationDialog from "../../components/SoftConfirmationDialog";
import { Skeleton } from "../../components/shared/Loading";

import { GET_BID_QUALITY_SUBMISSIONS, DELETE_USER_KPI } from "../../api/kpi";
import { toErrorMessage } from "../../helpers/errorText";
import { UseUsersDictionary } from "../../hooks/company";
import { BID_QUALITY_QUESTIONS, readStoredAnswer } from "../../constants/bidQuality";

import { T, FONT, CARD_SX, CARD_TITLE_SX, MICRO_LABEL_SX, FIGURE_SX, TONE, scoreBand } from "./bidQualityTokens";
import { BidQualityTabs, SectionCard, AnswerPill, ScoreChip, Notice } from "./BidQualityChrome";
import SubmissionDetailDialog from "./SubmissionDetailDialog";

const PAGE_SIZE = 50;

const inputFieldStyle = {
  borderRadius: "8px",
  border: `1px solid ${T.fieldLine}`,
  backgroundColor: "#F6F7F9 !important",
  color: "#1d2939 !important",
  minHeight: "44px",
};

/**
 * CSS Grid, not an HTML table: every cell (header AND body) shares the exact
 * same `grid-template-columns` track list, so columns are structurally forced
 * to line up.
 *
 * The two free-text columns (Job, Comments) are the only flexible tracks: they
 * share whatever room is left and clamp their text, so a long job name can't
 * push the table wider than the card and shove the delete button off screen.
 * When the card is narrower than the fixed columns need, the card body scrolls
 * sideways and the delete column stays pinned to the right edge (sticky).
 */
const QUESTION_COLUMN_WIDTH = (label) => Math.max(80, Math.round(label.length * 8.5 + 34));
const QUESTION_WIDTHS = BID_QUALITY_QUESTIONS.map((q) => QUESTION_COLUMN_WIDTH(q.shortLabel));
// Narrowest the grid may get before the card scrolls sideways instead.
const TABLE_MIN_WIDTH = 132 + 184 + 170 + QUESTION_WIDTHS.reduce((a, b) => a + b, 0) + 92 + 220 + 64;
const GRID_TEMPLATE_COLUMNS = [
  "132px", // submitted (date over time)
  "184px", // sales rep
  "minmax(170px, 1fr)", // job
  ...QUESTION_WIDTHS.map((w) => `${w}px`),
  "92px", // score
  "minmax(220px, 1.4fr)", // comments
  "64px", // delete
].join(" ");

const CENTERED = { justifyContent: "center", textAlign: "center" };

const headerCellSx = {
  display: "flex",
  alignItems: "center",
  fontFamily: FONT,
  fontWeight: 600,
  fontSize: "11.5px",
  letterSpacing: "0.03em",
  textTransform: "uppercase",
  color: T.muted,
  padding: "12px 16px",
  backgroundColor: T.subtle,
  borderBottom: `1px solid ${T.line}`,
  whiteSpace: "nowrap",
  minWidth: 0,
};
const bodyCellSx = {
  display: "flex",
  alignItems: "center",
  fontFamily: FONT,
  fontSize: "13px",
  padding: "10px 16px",
  color: T.ink2,
  backgroundColor: T.surface,
  borderBottom: `1px solid ${T.hairline}`,
  minHeight: "60px",
  minWidth: 0,
  transition: "background-color 120ms ease-out",
};
// Pinned to the right edge of the scrolling card so Delete is reachable at any
// zoom level or window width instead of hiding off the end of the table.
const stickyEndSx = {
  position: "sticky",
  right: 0,
  zIndex: 2,
  justifyContent: "center",
  boxShadow: "-10px 0 10px -10px rgba(16,24,40,0.14)",
};

/** Builds a CSV file and triggers a browser download — no server round-trip. */
const downloadCsv = (rows) => {
  const headers = [
    "Submitted at",
    "Sales Rep",
    "Job Name",
    ...BID_QUALITY_QUESTIONS.map((q) => q.label),
    "Score",
    "Comments",
  ];
  const csvEscape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [headers.map(csvEscape).join(",")];
  rows.forEach((r) => {
    const meta = r.metadata || {};
    const rep = r.user ? [r.user.firstName, r.user.lastName].filter(Boolean).join(" ") : (meta.salesRepName || "");
    lines.push(
      [
        moment(r.createdAt).format("YYYY-MM-DD HH:mm"),
        rep,
        meta.jobName || "",
        ...BID_QUALITY_QUESTIONS.map((q) => (readStoredAnswer(meta[q.key]) ? "Yes" : "No")),
        meta.score ?? "",
        meta.comments || "",
      ]
        .map(csvEscape)
        .join(","),
    );
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `bid-quality-submissions-${moment().format("YYYY-MM-DD")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/* -------------------------------------------------------------------------- */

/** Small danger-tinted icon button — the delete trigger on both the table row and the mobile card. */
const DeleteRowButton = ({ onClick, label }) => (
  <Tooltip title="Delete submission" placement="top" arrow>
    <SoftBox
      component="button"
      type="button"
      onClick={onClick}
      aria-label={label}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "30px",
        height: "30px",
        flexShrink: 0,
        borderRadius: "8px",
        border: `1px solid ${T.line}`,
        backgroundColor: T.surface,
        color: T.muted,
        cursor: "pointer",
        transition: "background-color 150ms ease-out, border-color 150ms ease-out, color 150ms ease-out",
        "&:hover": { backgroundColor: TONE.bad.fill, borderColor: TONE.bad.border, color: TONE.bad.ink },
      }}
    >
      <DeleteOutlineRoundedIcon sx={{ fontSize: "1.05rem" }} />
    </SoftBox>
  </Tooltip>
);

/**
 * A clamped piece of free text that opens the full submission. The hover fill
 * and the little expand glyph are the affordance: without them a truncated
 * cell just looks like text that got cut off.
 */
const ExpandableText = ({ text, onOpen, lines = 2, strong = false }) => (
  <Tooltip title="Click to view the full submission" placement="top" arrow enterDelay={500}>
    <SoftBox
      component="button"
      type="button"
      onClick={onOpen}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        minWidth: 0,
        width: "calc(100% + 20px)",
        margin: "-6px -10px",
        padding: "6px 10px",
        border: "none",
        borderRadius: "8px",
        background: "transparent",
        cursor: "pointer",
        textAlign: "left",
        fontFamily: FONT,
        fontSize: "13px",
        fontWeight: strong ? 600 : 400,
        color: strong ? T.ink : T.ink2,
        transition: "background-color 120ms ease-out",
        "& .expand-glyph": { opacity: 0, transition: "opacity 120ms ease-out" },
        "&:hover, &:focus-visible": { backgroundColor: T.hairline, outline: "none" },
        "&:hover .expand-glyph, &:focus-visible .expand-glyph": { opacity: 1 },
      }}
    >
      <SoftBox
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          display: "-webkit-box",
          WebkitLineClamp: lines,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          overflowWrap: "anywhere",
          lineHeight: 1.45,
        }}
      >
        {text}
      </SoftBox>
      <OpenInFullRoundedIcon className="expand-glyph" sx={{ fontSize: "0.85rem", color: T.faint, flexShrink: 0 }} />
    </SoftBox>
  </Tooltip>
);

/** One headline figure. Same frame and type scale as the dashboard stat tiles. */
const StatTile = ({ label, value, caption, tone, loading }) => (
  <SoftBox sx={{ ...CARD_SX, padding: "14px 16px" }}>
    <SoftTypography sx={{ ...MICRO_LABEL_SX, textTransform: "uppercase", letterSpacing: "0.04em", fontSize: "11px" }}>
      {label}
    </SoftTypography>
    {loading ? (
      <SoftBox sx={{ marginTop: "8px" }}>
        <Skeleton width="60%" height={22} />
      </SoftBox>
    ) : (
      <SoftTypography
        sx={{ ...FIGURE_SX, fontSize: "22px", fontWeight: 600, color: tone || T.ink, marginTop: "4px", lineHeight: 1.2 }}
      >
        {value}
      </SoftTypography>
    )}
    {caption && (
      <SoftTypography sx={{ ...MICRO_LABEL_SX, fontSize: "11.5px", marginTop: "2px" }}>{caption}</SoftTypography>
    )}
  </SoftBox>
);

/** A submission as a card — the shape the table collapses to below md. */
const SubmissionCard = ({ row, avatar, onDelete, onOpen }) => {
  const meta = row.metadata || {};
  const rep = row.user ? [row.user.firstName, row.user.lastName].filter(Boolean).join(" ") : (meta.salesRepName || "Unknown");
  return (
    <SoftBox sx={{ ...CARD_SX, padding: "14px 16px" }}>
      <SoftBox sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
        <SoftBox sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
          <SoftProfile src={avatar} name={rep} width="36px" height="36px" fullCover />
          <SoftBox sx={{ minWidth: 0 }}>
            <SoftTypography
              sx={{ fontFamily: FONT, fontSize: "13.5px", fontWeight: 600, color: T.ink, lineHeight: 1.3 }}
            >
              {rep}
            </SoftTypography>
            <SoftTypography sx={{ ...MICRO_LABEL_SX, fontSize: "11.5px" }}>
              {moment(row.createdAt).format("MMM D, YYYY · h:mm A")}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
        <SoftBox sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
          <ScoreChip score={meta.score} />
          <DeleteRowButton onClick={() => onDelete(row)} label={`Delete ${meta.jobName || "this"} submission`} />
        </SoftBox>
      </SoftBox>

      <SoftBox sx={{ marginTop: "10px" }}>
        <ExpandableText text={meta.jobName || "Unnamed job"} onOpen={() => onOpen(row)} strong lines={3} />
      </SoftBox>

      <SoftBox sx={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "10px" }}>
        {BID_QUALITY_QUESTIONS.map((q) => {
          const answer = readStoredAnswer(meta[q.key]);
          return (
            <SoftBox key={q.key} sx={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
              <AnswerPill value={answer} iconOnly />
              <SoftTypography sx={{ ...MICRO_LABEL_SX, fontSize: "11.5px" }}>{q.shortLabel}</SoftTypography>
            </SoftBox>
          );
        })}
      </SoftBox>

      {meta.comments && (
        <SoftBox sx={{ marginTop: "12px", paddingTop: "10px", borderTop: `1px solid ${T.hairline}`, color: T.muted }}>
          <ExpandableText text={meta.comments} onOpen={() => onOpen(row)} lines={3} />
        </SoftBox>
      )}
    </SoftBox>
  );
};

/* -------------------------------------------------------------------------- */

/**
 * Admin-only review table for native Bid Quality form submissions — the
 * in-app replacement for GHL's form "Submissions" tab. Sits on the same
 * PageHeader chrome as the form it reviews, and collapses from a wide grid to
 * stacked cards below md, where a 10-column table is unreadable.
 */
const BidQualityReviews = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [detailRow, setDetailRow] = useState(null);

  const isMobile = useMediaQuery((t) => t.breakpoints.down("sm"));
  const isCompact = useMediaQuery((t) => t.breakpoints.down("md"));
  const usersDict = UseUsersDictionary();
  const searchRef = useRef(null);

  const load = (opts = {}) => {
    setLoading(true);
    setError("");
    GET_BID_QUALITY_SUBMISSIONS({
      dateFrom: opts.dateFrom ?? dateFrom,
      dateTo: opts.dateTo ?? dateTo,
      search: opts.search ?? search,
      page: opts.page ?? page,
      limit: PAGE_SIZE,
    })
      .then((res) => {
        const data = res?.data ?? res;
        setRows(data?.docs || []);
        setTotal(data?.total || 0);
        setTotalPages(data?.totalPages || 1);
      })
      .catch((err) => setError(toErrorMessage(err, "Could not load submissions")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debouncedSearch = debounce((value) => {
    setSearch(value);
    setPage(1);
    load({ search: value, page: 1 });
  }, 400);

  const hasFilters = Boolean(search || dateFrom || dateTo);

  const clearFilters = () => {
    setSearch("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
    if (searchRef.current) searchRef.current.value = "";
    load({ search: "", dateFrom: "", dateTo: "", page: 1 });
  };

  // Averages are computed from the rows actually on screen, never from the
  // server-wide `total` — the endpoint only returns one page of documents, so
  // anything beyond a count would be a guess. The captions say "on this page"
  // so the number is never read as a company-wide average.
  const pageStats = useMemo(() => {
    const scores = rows
      .map((r) => Number(r.metadata?.score))
      .filter((n) => Number.isFinite(n));
    if (!scores.length) return { average: null, perfect: 0 };
    const sum = scores.reduce((a, b) => a + b, 0);
    return {
      average: Math.round((sum / scores.length) * 10) / 10,
      perfect: scores.filter((n) => n >= 100).length,
    };
  }, [rows]);

  const firstRowIndex = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastRowIndex = (page - 1) * PAGE_SIZE + rows.length;

  const avatarFor = (row) => usersDict?.[String(row.ownerId)]?.image || row?.user?.image;

  const repNameFor = (row) => {
    const meta = row?.metadata || {};
    return row?.user ? [row.user.firstName, row.user.lastName].filter(Boolean).join(" ") : (meta.salesRepName || "this rep");
  };

  const closeDeleteConfirm = () => {
    if (deleting) return; // don't let the dialog close mid-request
    setDeleteTarget(null);
  };

  // Deleting the source EmployeeKpi entry (DELETE /ekpi/:id) already triggers
  // refreshBidAccuracyForForm server-side, so the rep's Bid Accuracy average
  // recomputes on its own — this only needs to refresh what THIS page shows.
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const toastId = toast.loading("Deleting...");
    try {
      const result = await DELETE_USER_KPI(deleteTarget._id);
      if (result?.error) throw new Error(result.error.message || result.error);
      toast.success("Submission deleted", { id: toastId });
      setDeleteTarget(null);
      // Deleting the last row on a page beyond the first would otherwise
      // reload into an empty page — step back a page when that happens.
      const isLastRowOnPage = rows.length === 1 && page > 1;
      const nextPage = isLastRowOnPage ? page - 1 : page;
      if (isLastRowOnPage) setPage(nextPage);
      load({ page: nextPage });
    } catch (err) {
      toast.error(toErrorMessage(err, "Could not delete submission"), { id: toastId });
    } finally {
      setDeleting(false);
    }
  };

  /* -------------------------------- toolbar -------------------------------- */

  const toolbar = (
    <SoftBox sx={{ ...CARD_SX, padding: "16px 20px" }}>
      <SoftBox
        sx={{
          display: "grid",
          gridTemplateColumns: { mini: "1fr", sm: "minmax(0, 1fr) 150px 150px" },
          gap: "12px",
          alignItems: "end",
        }}
      >
        <SoftBox>
          <SoftTypography component="label" sx={{ ...MICRO_LABEL_SX, display: "block", marginBottom: "6px" }}>
            Search
          </SoftTypography>
          {/* SoftInput's own `icon` prop swaps in a different root element
              that this page's field styling wouldn't land on, so the icon is
              overlaid instead and the input just reserves room for it. */}
          <SoftBox sx={{ position: "relative" }}>
            <SearchRoundedIcon
              aria-hidden="true"
              sx={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "1.1rem",
                color: T.faint,
                pointerEvents: "none",
                zIndex: 1,
              }}
            />
            <SoftInput
              inputRef={searchRef}
              placeholder="Job name or sales rep"
              defaultValue={search}
              onChange={(e) => debouncedSearch(e.target.value)}
              sx={{ ...inputFieldStyle, paddingLeft: "34px" }}
            />
          </SoftBox>
        </SoftBox>
        <SoftBox>
          <SoftTypography component="label" sx={{ ...MICRO_LABEL_SX, display: "block", marginBottom: "6px" }}>
            From
          </SoftTypography>
          <SoftInput
            type="date"
            value={dateFrom}
            inputProps={{ max: dateTo || undefined }}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
              load({ dateFrom: e.target.value, page: 1 });
            }}
            sx={inputFieldStyle}
          />
        </SoftBox>
        <SoftBox>
          <SoftTypography component="label" sx={{ ...MICRO_LABEL_SX, display: "block", marginBottom: "6px" }}>
            To
          </SoftTypography>
          <SoftInput
            type="date"
            value={dateTo}
            inputProps={{ min: dateFrom || undefined }}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
              load({ dateTo: e.target.value, page: 1 });
            }}
            sx={inputFieldStyle}
          />
        </SoftBox>
      </SoftBox>

      {hasFilters && (
        <SoftBox sx={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "12px" }}>
          <SoftTypography sx={{ ...MICRO_LABEL_SX, fontSize: "12px" }}>
            Filters are narrowing this list.
          </SoftTypography>
          <SoftBox
            component="button"
            type="button"
            onClick={clearFilters}
            sx={{
              border: "none",
              background: "none",
              padding: 0,
              cursor: "pointer",
              fontFamily: FONT,
              fontSize: "12px",
              fontWeight: 600,
              color: T.ink,
              textDecoration: "underline",
            }}
          >
            Clear all
          </SoftBox>
        </SoftBox>
      )}
    </SoftBox>
  );

  /* --------------------------------- states -------------------------------- */

  const emptyState = (
    <SoftBox
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        padding: "48px 24px",
      }}
    >
      <SoftBox
        sx={{
          width: "48px",
          height: "48px",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: T.hairline,
          color: T.faint,
        }}
      >
        <InboxRoundedIcon sx={{ fontSize: "1.6rem" }} />
      </SoftBox>
      <SoftTypography sx={{ fontFamily: FONT, fontSize: "15px", fontWeight: 600, color: T.ink, marginTop: "14px" }}>
        {hasFilters ? "Nothing matches these filters" : "No submissions yet"}
      </SoftTypography>
      <SoftTypography
        sx={{ fontFamily: FONT, fontSize: "13px", fontWeight: 400, color: T.muted, marginTop: "6px", maxWidth: "38ch", lineHeight: 1.5 }}
      >
        {hasFilters
          ? "Try a wider date range, or clear the filters to see every job that has been rated."
          : "Scores land here the moment a technician rates a bid on the Rate a job tab."}
      </SoftTypography>
      {hasFilters && (
        <SoftButton
          variant="outlined"
          color="primary"
          onClick={clearFilters}
          sx={{ fontFamily: FONT, textTransform: "none", borderRadius: "10px", fontWeight: 600, marginTop: "18px" }}
        >
          Clear filters
        </SoftButton>
      )}
    </SoftBox>
  );

  const skeletonRows = Array.from({ length: 6 });

  const listCaption =
    loading || total === 0 ? "" : `Showing ${firstRowIndex}–${lastRowIndex} of ${total.toLocaleString()}`;

  const exportButton = (
    <Tooltip title="Downloads the rows currently shown" placement="top" arrow>
      <span>
        <SoftButton
          variant="outlined"
          color="primary"
          size="small"
          onClick={() => downloadCsv(rows)}
          disabled={loading || rows.length === 0}
          sx={{ fontFamily: FONT, textTransform: "none", borderRadius: "8px", fontWeight: 600, whiteSpace: "nowrap" }}
          startIcon={<FileDownloadOutlinedIcon />}
        >
          {totalPages > 1 ? "Export page" : "Export CSV"}
        </SoftButton>
      </span>
    </Tooltip>
  );

  /* ---------------------------------- table -------------------------------- */

  const table = (
    <SoftBox>
      <SoftBox
        sx={{
          display: "grid",
          gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
          width: "100%",
          minWidth: `${TABLE_MIN_WIDTH}px`,
        }}
      >
        <SoftBox sx={headerCellSx}>Submitted</SoftBox>
        <SoftBox sx={headerCellSx}>Sales rep</SoftBox>
        <SoftBox sx={headerCellSx}>Job</SoftBox>
        {BID_QUALITY_QUESTIONS.map((q) => (
          <Tooltip key={q.key} title={q.title} placement="top" arrow>
            <SoftBox sx={{ ...headerCellSx, ...CENTERED }}>{q.shortLabel}</SoftBox>
          </Tooltip>
        ))}
        <SoftBox sx={{ ...headerCellSx, ...CENTERED }}>Score</SoftBox>
        <SoftBox sx={headerCellSx}>Comments</SoftBox>
        <SoftBox sx={{ ...headerCellSx, ...stickyEndSx }} aria-hidden="true" />

        {loading &&
          skeletonRows.map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={`skeleton-${i}`}>
              <SoftBox sx={bodyCellSx}><Skeleton width="80%" /></SoftBox>
              <SoftBox sx={bodyCellSx}><Skeleton width="70%" /></SoftBox>
              <SoftBox sx={bodyCellSx}><Skeleton width="75%" /></SoftBox>
              {BID_QUALITY_QUESTIONS.map((q) => (
                <SoftBox key={q.key} sx={{ ...bodyCellSx, ...CENTERED }}><Skeleton variant="circle" width={22} /></SoftBox>
              ))}
              <SoftBox sx={{ ...bodyCellSx, ...CENTERED }}><Skeleton width="50%" /></SoftBox>
              <SoftBox sx={bodyCellSx}><Skeleton width="85%" /></SoftBox>
              <SoftBox sx={{ ...bodyCellSx, ...stickyEndSx }}><Skeleton variant="circle" width={24} /></SoftBox>
            </React.Fragment>
          ))}

        {!loading &&
          rows.map((r) => {
            const meta = r.metadata || {};
            const rep = r.user ? [r.user.firstName, r.user.lastName].filter(Boolean).join(" ") : (meta.salesRepName || "Unknown");
            const submitted = moment(r.createdAt);
            return (
              // `display: contents` keeps every cell a direct grid item while
              // letting the row react to hover as one unit.
              <SoftBox
                key={r._id}
                sx={{
                  display: "contents",
                  "&:hover > div": { backgroundColor: T.subtle },
                }}
              >
                <SoftBox
                  sx={{
                    ...bodyCellSx,
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    gap: "1px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span style={{ fontWeight: 600, color: T.ink2 }}>{submitted.format("MMM D, YYYY")}</span>
                  <span style={{ fontSize: "12px", color: T.faint }}>{submitted.format("h:mm A")}</span>
                </SoftBox>
                <SoftBox sx={{ ...bodyCellSx, gap: "10px" }}>
                  <SoftProfile src={avatarFor(r)} name={rep} width="28px" height="28px" fullCover />
                  <SoftTypography
                    sx={{
                      fontFamily: FONT,
                      fontSize: "13px",
                      fontWeight: 600,
                      color: T.ink,
                      lineHeight: 1.3,
                      minWidth: 0,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {rep}
                  </SoftTypography>
                </SoftBox>
                <SoftBox sx={bodyCellSx}>
                  {meta.jobName ? (
                    <ExpandableText text={meta.jobName} onOpen={() => setDetailRow(r)} strong />
                  ) : (
                    <span style={{ color: T.faint }}>—</span>
                  )}
                </SoftBox>
                {BID_QUALITY_QUESTIONS.map((q) => (
                  <SoftBox key={q.key} sx={{ ...bodyCellSx, ...CENTERED }}>
                    <AnswerPill value={readStoredAnswer(meta[q.key])} iconOnly />
                  </SoftBox>
                ))}
                <SoftBox sx={{ ...bodyCellSx, ...CENTERED }}>
                  <ScoreChip score={meta.score} size="sm" />
                </SoftBox>
                <SoftBox sx={bodyCellSx}>
                  {meta.comments ? (
                    <ExpandableText text={meta.comments} onOpen={() => setDetailRow(r)} />
                  ) : (
                    <span style={{ color: T.faint }}>—</span>
                  )}
                </SoftBox>
                <SoftBox sx={{ ...bodyCellSx, ...stickyEndSx }}>
                  <DeleteRowButton
                    onClick={() => setDeleteTarget(r)}
                    label={`Delete ${meta.jobName || "this"} submission`}
                  />
                </SoftBox>
              </SoftBox>
            );
          })}
      </SoftBox>

      {!loading && rows.length === 0 && emptyState}
    </SoftBox>
  );

  const cards = (
    <SoftBox sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {loading &&
        skeletonRows.slice(0, 4).map((_, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <SoftBox key={`skeleton-card-${i}`} sx={{ ...CARD_SX, padding: "14px 16px" }}>
            <Skeleton width="55%" height={18} />
            <SoftBox sx={{ marginTop: "10px" }}>
              <Skeleton width="80%" />
            </SoftBox>
            <SoftBox sx={{ marginTop: "10px" }}>
              <Skeleton variant="bar" height={8} />
            </SoftBox>
          </SoftBox>
        ))}
      {!loading && rows.map((r) => (
        <SubmissionCard key={r._id} row={r} avatar={avatarFor(r)} onDelete={setDeleteTarget} onOpen={setDetailRow} />
      ))}
      {!loading && rows.length === 0 && <SoftBox sx={CARD_SX}>{emptyState}</SoftBox>}
    </SoftBox>
  );

  /* --------------------------------- render -------------------------------- */

  return (
    <>
      <PageHeader
        headerText="Job Bid Quality"
        subheaderText="Every bid a technician has rated, newest first."
        isMobile={isMobile}
        tabs={<BidQualityTabs active="reviews" />}
      />

      <SoftBox
        sx={{
          margin: { mini: "0.5rem 1rem 1rem 1rem", sm: "1rem 1.5rem 1.5rem 1.5rem" },
          display: "flex",
          flexDirection: "column",
          gap: { mini: "14px", sm: "20px" },
        }}
      >
        <SoftBox
          sx={{
            display: "grid",
            gridTemplateColumns: { mini: "1fr", sm: "repeat(3, 1fr)" },
            gap: { mini: "10px", sm: "16px" },
          }}
        >
          <StatTile
            label="Jobs rated"
            value={total.toLocaleString()}
            caption={hasFilters ? "matching your filters" : "all time"}
            loading={loading}
          />
          <StatTile
            label="Average score"
            value={pageStats.average === null ? "—" : `${pageStats.average}%`}
            caption="on this page"
            tone={pageStats.average === null ? T.faint : scoreBand(pageStats.average).ink}
            loading={loading}
          />
          <StatTile
            label="Perfect bids"
            value={rows.length ? `${pageStats.perfect}/${rows.length}` : "—"}
            caption="scored 100% on this page"
            loading={loading}
          />
        </SoftBox>

        {toolbar}

        {error && (
          <Notice
            tone="bad"
            title="Couldn't load submissions"
            action={
              <SoftButton
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => load()}
                sx={{ fontFamily: FONT, textTransform: "none", borderRadius: "8px", fontWeight: 600, whiteSpace: "nowrap" }}
                startIcon={<RefreshRoundedIcon />}
              >
                Retry
              </SoftButton>
            }
          >
            {error}
          </Notice>
        )}

        {/* Below md the grid becomes stacked cards, which carry their own
            frame — so the list head drops the card shell there instead of
            leaving a titled strip floating above frameless content. */}
        {isCompact ? (
          <SoftBox sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <SoftBox sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
              <SoftBox sx={{ minWidth: 0 }}>
                <SoftTypography sx={CARD_TITLE_SX}>All submissions</SoftTypography>
                {listCaption && (
                  <SoftTypography sx={{ ...MICRO_LABEL_SX, fontSize: "12px", marginTop: "2px" }}>
                    {listCaption}
                  </SoftTypography>
                )}
              </SoftBox>
              {exportButton}
            </SoftBox>
            {cards}
          </SoftBox>
        ) : (
          <SectionCard
            title="All submissions"
            caption={listCaption}
            action={exportButton}
            bodySx={{ padding: 0, overflow: "auto" }}
          >
            {table}
          </SectionCard>
        )}

        {totalPages > 1 && (
          <SoftBox sx={{ display: "flex", justifyContent: "center" }}>
            <Pagination
              count={totalPages}
              page={page}
              shape="rounded"
              onChange={(e, value) => {
                setPage(value);
                load({ page: value });
              }}
              sx={{
                "& .MuiPaginationItem-root": {
                  fontFamily: FONT,
                  fontSize: "13px",
                  fontWeight: 600,
                  color: T.muted,
                  borderRadius: "8px",
                },
              }}
            />
          </SoftBox>
        )}
      </SoftBox>

      <SubmissionDetailDialog
        row={detailRow}
        repName={detailRow ? repNameFor(detailRow) : ""}
        avatar={detailRow ? avatarFor(detailRow) : undefined}
        onClose={() => setDetailRow(null)}
      />

      {deleteTarget && (
        <SoftConfirmationDialog
          open={Boolean(deleteTarget)}
          onClose={closeDeleteConfirm}
          type="error"
          icon={<WarningAmberIcon fontSize="large" sx={{ color: TONE.bad.ink }} />}
          title="Delete this submission?"
          description={
            <SoftBox display="flex" flexDirection="column" alignItems="center" mt="20px" mb="8px" px={1}>
              <SoftTypography
                sx={{ fontFamily: FONT, fontSize: "14px", fontWeight: 500, color: T.ink2, textAlign: "center", lineHeight: 1.5 }}
              >
                This permanently removes <strong>{deleteTarget.metadata?.jobName || "this job"}</strong>&apos;s score for{" "}
                <strong>{repNameFor(deleteTarget)}</strong>.
              </SoftTypography>
              <SoftTypography
                sx={{ fontFamily: FONT, fontSize: "13px", fontWeight: 400, color: T.muted, textAlign: "center", lineHeight: 1.5, mt: "8px" }}
              >
                Their Bid Accuracy average recalculates immediately without it. This can&apos;t be undone.
              </SoftTypography>
            </SoftBox>
          }
          footer={
            <SoftBox display="flex" gap="10px" justifyContent="center" mt="24px">
              <SoftButton
                variant="outlined"
                color="primary"
                onClick={closeDeleteConfirm}
                disabled={deleting}
                sx={{ fontFamily: FONT, textTransform: "none", borderRadius: "10px", fontWeight: 600, minWidth: "120px" }}
              >
                Cancel
              </SoftButton>
              <SoftButton
                variant="contained"
                onClick={confirmDelete}
                disabled={deleting}
                sx={{
                  fontFamily: FONT,
                  textTransform: "none",
                  borderRadius: "10px",
                  fontWeight: 600,
                  minWidth: "120px",
                  backgroundColor: TONE.bad.ink,
                  color: "#fff",
                  "&:hover": { backgroundColor: TONE.bad.ink, filter: "brightness(0.92)" },
                }}
              >
                {deleting ? "Deleting..." : "Delete"}
              </SoftButton>
            </SoftBox>
          }
        />
      )}
    </>
  );
};

export default BidQualityReviews;
